import { AppSyncFarm, MonitorVaults } from 'common/models/farm';
import { AppSyncUserInfo } from 'common/models/userInfo';
import debounce from 'lodash/debounce';
import React from 'react';
import { FarmUtils } from 'utils/farmUtils';
import { TokenUtils } from 'utils/tokenUtils';
import { ClaimEvent } from 'xstate/claimEvent';
import {
  claimService,
  EventUpdateAccountInfoArgs,
  initialClaimContext,
} from 'xstate/claimMachine';
import { RemoteTxEvent } from 'xstate/txMachine';
import { XStateUtils } from 'xstate/xStateUtils';
import UserDepositsGroup from './UserDepositsGroup';
import useSelectedChain from 'hooks/useSelectedChain';
import { useAccount } from 'wagmi';
import { ClaimApi } from 'sdk/claimApi';

type Props = {
  userInfo: AppSyncUserInfo;
  farms: AppSyncFarm[];
  userInfoLoading: boolean;
  refetchUserInfo: () => Promise<void>;
  isIchiVault: boolean;
  monitorVaults: MonitorVaults[];
  searchValue?: string;
  selectedTokenAddress: string;
  backToVaults: () => void;
};

const UserDepositsTable: React.FC<Props> = (props) => {
  const { chainId } = useSelectedChain();
  const { address: accountAddress } = useAccount();

  const [poolId, setPoolId] = React.useState<number>();
  const [isClaiming, setIsClaiming] = React.useState<boolean>(
    initialClaimContext.isClaiming
  );

  const refreshData = async () => {
    await props.refetchUserInfo();
  };
  const debouncedRefreshData = React.useMemo(
    () => debounce(refreshData, 750),
    []
  );

  React.useEffect(() => {
    const subscription = claimService.subscribe((state) => {
      if (state.context.poolId !== poolId) {
        setPoolId(state.context.poolId);
      }

      if (state.context.isClaiming !== isClaiming) {
        setIsClaiming(state.context.isClaiming);
      }
    });

    claimService.onEvent((e) => {
      if (e.type === RemoteTxEvent.REMOTE_TX_SUCCESS) {
        void debouncedRefreshData();
      }
    });

    return subscription.unsubscribe;
  }, [claimService, poolId, isClaiming]);

  // Update the account info once it's set
  React.useEffect(() => {
    // Whenever the wallet is connected, update the account info in the claimMachine
    // which may trigger other states, like checking for existing transactions
    if (ClaimApi.Claim.getInstance() && chainId && accountAddress) {
      const payload: EventUpdateAccountInfoArgs = {
        address: accountAddress,
        chainId: chainId,
      };
      claimService.send(
        ClaimEvent.UPDATE_ACCOUNT_INFO,
        XStateUtils.createEventPayload(payload)
      );
    }
  }, [chainId, accountAddress]);

  const baseTokens = props.farms.map((v) => FarmUtils.getBaseToken(v));
  const baseTokenAddresses = baseTokens.map((t) => t.address.toLowerCase());
  let allTokens = baseTokens
    .slice()
    .filter((v, index) => baseTokenAddresses.indexOf(v.address.toLowerCase()) === index);

  if (props.selectedTokenAddress) {
    allTokens = allTokens.filter(
      (v) => v.address.toLowerCase() === props.selectedTokenAddress.toLowerCase()
    );
  }

  const totalDeposits = props.farms.reduce((total, curr) => {
    return total + TokenUtils.getFarmUserInfo(curr, props.userInfo).totalLPUSD;
  }, 0);
  if (totalDeposits === 0 && !props.selectedTokenAddress) {
    return (
      <div className='primary-text-color'>
        Your currently connected wallet has no deposits. To deposit, click the
        "Vaults" tab and select the token you would like to deposit by clicking
        the Earn button.
      </div>
    );
  }

  return (
    <>
      {props.selectedTokenAddress && (
        <button
          onClick={() => props.backToVaults()}
          className="go-back py-4 primary-color font-semibold"
        >
          <span className="arrow-left-green"> </span> Back to Vaults
        </button>
      )}
      {allTokens?.map((token) => (
        <UserDepositsGroup
          key={`deposits-${token.address}`}
          token={token}
          farms={props.farms.filter(
            (v) => FarmUtils.getBaseToken(v).address.toLowerCase() === token.address.toLowerCase()
          )}
          userInfoLoading={props.userInfoLoading}
          userInfo={props.userInfo}
          chainId={chainId}
          claiming={false}
          isIchiVault={props.isIchiVault}
          monitorVaults={props.monitorVaults}
          selectedTokenAddress={props.selectedTokenAddress}
        />
      ))}
    </>
  );
};

export default UserDepositsTable;
